import React from 'react';
import { motion } from 'framer-motion';

const TermsOfService: React.FC = () => {
  return (
    <div className="legal-page">
      <section className="page-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Terms of Service</h1>
            <p>Please read these terms carefully before using our services. Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="legal-content">
        <div className="container">
          <div className="legal-wrapper">

            <div className="legal-section">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By using the website and services of Pack N Fly ("we", "our", "us"), you agree to 
                be bound by these Terms of Service. If you do not agree with any part of these terms, 
                you should not use our website or services.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. Services</h2>
              <p>Pack N Fly provides international courier and shipping services including:</p>
              <ul>
                <li>Document courier to international destinations</li>
                <li>Parcel and package shipping worldwide</li>
                <li>Medicine courier (subject to regulations)</li>
                <li>Food items courier (dry and non-perishable)</li>
                <li>Commercial shipment and bulk cargo</li>
                <li>Excess baggage shipping</li>
                <li>Doorstep pickup from Chandigarh, Mohali, Panchkula, and Zirakpur</li>
                <li>Professional packing and customs clearance assistance</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>3. Booking and Payments</h2>
              <ul>
                <li>All shipment bookings are subject to availability and acceptance by Pack N Fly.</li>
                <li>Shipping charges are based on actual or volumetric weight (whichever is higher), destination, and shipment type.</li>
                <li>Quoted prices are estimates and may vary based on final weight, dimensions, and customs duties.</li>
                <li>Payment must be made in full before or at the time of shipment pickup.</li>
                <li>We accept cash, UPI, bank transfer, and other digital payment methods.</li>
                <li>Customs duties, taxes, and import charges at the destination are the responsibility of the sender or receiver as applicable.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>4. Prohibited Items</h2>
              <p>The following items are strictly prohibited from shipping:</p>
              <ul>
                <li>Explosives, firearms, and ammunition</li>
                <li>Illegal drugs and narcotics</li>
                <li>Flammable liquids and hazardous chemicals</li>
                <li>Counterfeit currency or goods</li>
                <li>Live animals and perishable biological substances</li>
                <li>Pornographic material</li>
                <li>Any items prohibited by Indian export laws or destination country import laws</li>
              </ul>
              <p>
                Pack N Fly reserves the right to inspect shipments and refuse any package that 
                contains prohibited or restricted items.
              </p>
            </div>

            <div className="legal-section">
              <h2>5. Shipment Restrictions</h2>
              <h3>Medicine Courier</h3>
              <p>
                Medicines must be in original sealed packaging with a valid prescription. Shipment is 
                subject to the regulations of both India and the destination country. Certain medicines 
                may be restricted or prohibited in specific countries.
              </p>
              <h3>Food Items</h3>
              <p>
                Only dry, non-perishable food items are accepted (pickles, spices, sweets, dry snacks). 
                Fresh, frozen, or perishable food items are not accepted. All food items must comply with 
                the destination country's import regulations.
              </p>
              <h3>Commercial Shipments</h3>
              <p>
                Commercial shipments require proper invoices, packing lists, and export documentation. 
                We assist with customs paperwork but the accuracy of declared values and descriptions 
                is the sender's responsibility.
              </p>
            </div>

            <div className="legal-section">
              <h2>6. Delivery Timelines</h2>
              <ul>
                <li>Estimated delivery times are indicative and not guaranteed.</li>
                <li>Delivery may be delayed due to customs clearance, weather, holidays, or other factors beyond our control.</li>
                <li>Standard delivery: 5–8 business days (varies by destination).</li>
                <li>Express delivery: 3–5 business days (select destinations).</li>
                <li>Pack N Fly is not liable for delays caused by customs authorities or force majeure events.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>7. Tracking</h2>
              <p>
                All shipments are assigned a unique tracking number. You can track your shipment status 
                online or contact our support team for updates. We also provide regular updates via 
                WhatsApp and SMS.
              </p>
            </div>

            <div className="legal-section">
              <h2>8. Liability and Insurance</h2>
              <ul>
                <li>Pack N Fly takes utmost care in handling all shipments. However, our liability for loss or damage is limited to the declared value of the shipment or applicable carrier liability limits, whichever is lower.</li>
                <li>We recommend purchasing additional shipping insurance for high-value items.</li>
                <li>We are not liable for damage caused by inadequate packing if the sender chose to pack the items themselves.</li>
                <li>Claims for loss or damage must be filed within 7 days of delivery (or expected delivery date).</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>9. Cancellation and Refunds</h2>
              <ul>
                <li>Shipments can be cancelled before pickup for a full refund.</li>
                <li>Once picked up and in transit, cancellation may not be possible.</li>
                <li>If a shipment is returned due to incorrect address or receiver refusal, re-shipping charges will apply.</li>
                <li>Refunds, where applicable, will be processed within 7–10 business days.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>10. User Responsibilities</h2>
              <p>As a user of our services, you agree to:</p>
              <ul>
                <li>Provide accurate and complete shipment information</li>
                <li>Declare the correct value and contents of your shipment</li>
                <li>Ensure the shipment does not contain prohibited items</li>
                <li>Comply with all applicable export and import regulations</li>
                <li>Provide valid identification when required</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>11. Intellectual Property</h2>
              <p>
                All content on this website, including text, images, logos, and design, is the property 
                of Pack N Fly and is protected by copyright laws. You may not reproduce, distribute, or 
                use any content without our prior written consent.
              </p>
            </div>

            <div className="legal-section">
              <h2>12. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Pack N Fly shall not be liable for any indirect, 
                incidental, special, or consequential damages arising from the use of our services or 
                website, including but not limited to loss of profits, data, or business opportunities.
              </p>
            </div>

            <div className="legal-section">
              <h2>13. Governing Law</h2>
              <p>
                These Terms of Service are governed by the laws of India. Any disputes arising from 
                these terms or our services shall be subject to the exclusive jurisdiction of the 
                courts in Panchkula, Haryana, India.
              </p>
            </div>

            <div className="legal-section">
              <h2>14. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Service at any time. Changes will be 
                effective immediately upon posting on this page. Your continued use of our services 
                after any changes constitutes acceptance of the new terms.
              </p>
            </div>

            <div className="legal-section">
              <h2>15. Contact Us</h2>
              <p>For questions about these Terms of Service, please contact us:</p>
              <ul>
                <li><strong>Company:</strong> Pack N Fly</li>
                <li><strong>Email:</strong> <a href="mailto:packnfly96@gmail.com">packnfly96@gmail.com</a></li>
                <li><strong>Phone:</strong> <a href="tel:+918847428801">+91 8847428801</a></li>
                <li><strong>WhatsApp:</strong> <a href="https://wa.me/918847428801" target="_blank" rel="noopener noreferrer">+91 8847428801</a></li>
                <li><strong>Address:</strong> Plot No 5A, Industrial Area, Phase 2, Panchkula, Haryana</li>
              </ul>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
